<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { useSelectAppChain } from '@src/shared/lib/composables/useSelectAppChain';
import { vClickOutside } from '@src/shared/lib/directives/clickOutside';
import { useAppConfigStore } from '@src/shared/stores/app-config';

const store = useAppConfigStore();
const { selectChain } = useSelectAppChain();
const { chains, selectedChain, selectedChainCode } = storeToRefs(store);

const isOpen = ref(false);

/** Y-позиция низа мобильной кнопки — от неё раскрывается дропдаун во всю ширину экрана. */
const mobileButtonRef = ref<HTMLElement | null>(null);
const dropdownTop = ref(0);

function updateDropdownPosition() {
	const el = mobileButtonRef.value;
	if (!el) {
		return;
	}
	dropdownTop.value = el.getBoundingClientRect().bottom + 8;
}

function toggleDropdown() {
	isOpen.value = !isOpen.value;
	if (isOpen.value) {
		void nextTick(updateDropdownPosition);
	}
}

function closeDropdown() {
	isOpen.value = false;
}

function onViewportChange() {
	if (isOpen.value) {
		updateDropdownPosition();
	}
}

onMounted(() => {
	globalThis.addEventListener('resize', onViewportChange);
	globalThis.addEventListener('scroll', onViewportChange, true);
});

onBeforeUnmount(() => {
	globalThis.removeEventListener('resize', onViewportChange);
	globalThis.removeEventListener('scroll', onViewportChange, true);
});

function selectNetwork(code: string) {
	closeDropdown();
	void selectChain(code);
}
</script>

<template>
	<div v-click-outside="closeDropdown" class="relative" @keydown.esc="closeDropdown">
		<button
			v-if="selectedChain"
			type="button"
			class="hidden sm:flex items-center h-11 px-4 bg-cardBg border border-cardBorder transition-all hover:border-cardBorderHover rounded-full text-primaryText gap-2 cursor-pointer"
			aria-haspopup="true"
			:aria-expanded="isOpen"
			@click="toggleDropdown"
		>
			<img :src="selectedChain.iconUrl" :alt="selectedChain.name" class="w-5 h-5" />
			<span>{{ selectedChain.name }}</span>
			<img
				src="/src/shared/assets/icons/fill-arrow-dropdown.svg"
				class="w-2 transition-transform duration-200"
				:class="isOpen ? 'rotate-180' : 'rotate-0'"
				alt=""
			/>
		</button>

		<button
			v-if="selectedChain"
			ref="mobileButtonRef"
			type="button"
			class="flex sm:hidden items-center h-10 pl-3 pr-2.5 gap-1.5 bg-cardBg border border-cardBorder transition-all hover:border-cardBorderHover rounded-full text-primaryText cursor-pointer"
			aria-haspopup="true"
			:aria-expanded="isOpen"
			:aria-label="`Network: ${selectedChain.name}`"
			@click="toggleDropdown"
		>
			<img :src="selectedChain.iconUrl" :alt="selectedChain.name" class="w-5 h-5" />
			<img
				src="/src/shared/assets/icons/fill-arrow-dropdown.svg"
				class="w-2 transition-transform duration-200"
				:class="isOpen ? 'rotate-180' : 'rotate-0'"
				alt=""
			/>
		</button>

		<Transition name="dropdown">
			<ul
				v-if="isOpen"
				class="network-dropdown fixed inset-x-4 z-30 flex flex-col gap-1 rounded-xl border border-cardBorder bg-mainBg p-3 shadow-lg shadow-black/40 sm:absolute sm:inset-x-auto sm:right-0 sm:mt-2 sm:w-60"
				:style="{ '--dropdown-top': `${dropdownTop}px` }"
			>
				<li class="text-xs mb-1 text-secondaryText uppercase tracking-wide">Network</li>
				<li v-for="chain in chains" :key="chain.code">
					<button
						type="button"
						class="cursor-pointer flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors duration-150 hover:bg-white/10"
						:class="chain.code === selectedChainCode ? 'bg-white/10 text-primaryText' : 'text-secondaryText'"
						@click="selectNetwork(chain.code)"
					>
						<img :src="chain.iconUrl" :alt="chain.name" class="w-5 h-5" />
						<span>{{ chain.name }}</span>
					</button>
				</li>
			</ul>
		</Transition>
	</div>
</template>

<style scoped>
/* Мобильный дропдаун раскрывается фиксированно во всю ширину экрана — top от низа кнопки. */
.network-dropdown {
	top: var(--dropdown-top);
}

@media (min-width: 640px) {
	.network-dropdown {
		top: auto;
	}
}

.dropdown-enter-active,
.dropdown-leave-active {
	transition:
		opacity 0.15s ease,
		transform 0.15s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
	opacity: 0;
	transform: translateY(-4px);
}
</style>
